/// <amd-module name="VSS/Features/ReduxDirectory/Contracts" />
import { IHubViewAwareState } from "VSS/Redux/Deprecated/HubViewState/HubViewStateContracts";
import { IDirectoryArtifact, ExceptionInfo, IFavoriteData } from "VSS/Features/Directory/DirectoryContracts";

/** The artifact state for a directory page*/
export interface IDirectoryArtifactState<T extends IDirectoryArtifact> {
    /** All of the artifacts loaded at this time */
    artifacts: { [key: string]: T };
    /** The deleted artifacts (loaded from deleted favorites) */
    deletedArtifacts: { [key: string]: T };
    /** The ids of artifacts that the user is a member of */
    customKeyGroupings: { [key: string]: string[] };
    /** The ids of recent artifacts */
    recentArtifactKeys: string[];
    /** Indicator that the page can declare TTI has been achieved */
    readyToMarkTti: boolean;
}

/** Struct which contains contextual information about the current directory page*/
export interface IDirectoryContext {
    /** The favorite type of the artifact */
    favoriteType: string;
    /** The hub name to use for telemetry */
    hubName: string;
}

/** "UI State" for directory pages */
export interface IDirectoryUIState {
    /** Is the filter bar visible */
    isFilterBarVisible: boolean;
    /** Is the user navigating away? */
    isNavigating: boolean;
    /** Is the all section loading */
    isAllSectionLoading: boolean;
    /** Has the all section data been populated */
    isAllSectionPopulated: boolean;
    /** Data provider exception data */
    exceptionInfo?: ExceptionInfo;
    /** The collapsed state for row groups */
    rowGroupCollapsedMap: { [key: string]: boolean };
    /** Is New Dashboard Dirctory Loading */
    newDashboardDirectoryLoading : boolean
}

/** The directory specific state */
export interface IDirectoryState<T extends IDirectoryArtifact> {
    /** Contains all the artifact state */
    directoryArtifactState: IDirectoryArtifactState<T>;
    /** Contains state related to favorites */
    favoriteState: IFavoriteState;
    /** Contains the rest of the state */
    uiState: IDirectoryUIState;
}

export interface IDirectoryAwareState<T extends IDirectoryArtifact> {
    directoryState: IDirectoryState<T>;
}

/** The full state for all Directory pages */
export interface IDirectoryViewState<T extends IDirectoryArtifact> extends IDirectoryAwareState<T>, IHubViewAwareState {}

/** The favorite state for a directory page */
export interface IFavoriteState {
    /** All of the favorites loaded at this time*/
    favorites: { [key: string]: IFavoriteData };
    /** The status of a favorite */
    favoriteStatuses: { [key: string]: FavoriteStatus };
    /** Any errors associated with favorite actions */
    favoriteErrors: { [key: string]: Error };
    /** Permission governing whether the user can see favorites */
    favoritesViewPermission: boolean;
}

/**
 * An interface used by the directory grid to render artifacts with most recently used, favorites, all, and custom groupings.
 */
export interface IUnifiedDirectoryDataProviderData<T extends IDirectoryArtifact> extends IDirectoryDataProviderData<T> {
    /**
     * A dictionary of the name of each custom grouping to an array of artifact Ids that belong to that group.
     */
    customGroupings: {
        [groupName: string]: string[];
    };

    /**
     * An array of favorites to be used for the favorites group in the directory grid.
     */
    favorites: IFavoriteData[];

    /**
     * keys of objects that are most recently visited.
     */
    recentKeys: string[];
}

export interface IDirectoryDataProviderData<T extends IDirectoryArtifact> {
    artifacts: T[];
    exceptionInfo?: ExceptionInfo;
}

export const enum FavoriteStatus {
    Favorited = 1,
    Unfavorited = 2,
    Favoriting = 3,
    Unfavoriting = 4
}

// UI Contracts
export interface IDirectoryRowGroup<T extends IDirectoryArtifact> {
    alwaysVisible?: boolean;
    items: T[];
    groupKey: string;
    groupHeader: string;
    toggleable: boolean;
    collapsed?: boolean;
}
